import React from "react";
import { useTranslation } from "react-i18next";
import { Languages, BookOpen } from "lucide-react";
import { LANG_FLAGS } from "@/types/linguadeck";
import MorphemeBreakdown from "./MorphemeBreakdown";
import type { MorphologyParts } from "@/lib/philology/api";

interface RootWordCardProps {
  rootWord: string;
  rootMeaning?: string | null;
  etymology?: string | null;
  morphology?: MorphologyParts | null;
  language: string;
  level?: string | null;
  sourcePhrase?: string | null;
  showSwitchLanguage?: boolean;
  onSwitchLanguage?: () => void;
}

export default function RootWordCard({
  rootWord,
  rootMeaning,
  etymology,
  morphology,
  language,
  level,
  sourcePhrase,
  showSwitchLanguage,
  onSwitchLanguage,
}: RootWordCardProps) {
  const { t } = useTranslation();
  const flag = LANG_FLAGS[language] ?? "🌐";
  const hasSegments = !!(morphology?.segments && morphology.segments.length > 0);

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] p-5 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
        <span>{flag}</span>
        <span>{language.toUpperCase()}</span>
        {level && <span className="px-1.5 py-0.5 rounded-md bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))]">{level}</span>}
      </div>
      {hasSegments ? (
        <div className="mt-3" style={{ fontFamily: "Instrument Serif, serif" }}>
          <MorphemeBreakdown word={rootWord} morphology={morphology} size="lg" showLabels showLegend />
        </div>
      ) : (
        <h2 className="mt-2 text-3xl md:text-4xl font-bold text-[hsl(var(--foreground))] break-words leading-tight" style={{ fontFamily: "Instrument Serif, serif" }}>
          {rootWord || "—"}
        </h2>
      )}
      {morphology?.stem && (
        <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] leading-snug">
          <span className="font-semibold">{t("philology.stem")}:</span>{" "}
          <span className="font-mono text-[hsl(var(--foreground))]">{morphology.stem}</span>
        </div>
      )}
      {sourcePhrase && (
        <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] italic">
          {t("philology.phraseMode", { word: rootWord })}
        </div>
      )}
      {rootMeaning && (
        <div className="mt-3 flex items-start gap-2 text-sm text-[hsl(var(--foreground))]">
          <BookOpen size={16} className="shrink-0 mt-0.5 text-[hsl(var(--muted-foreground))]" />
          <p className="leading-snug">{rootMeaning}</p>
        </div>
      )}
      {etymology && (
        <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] leading-snug">
          <span className="font-semibold">{t("philology.etymology")}:</span> {etymology}
        </div>
      )}
      {showSwitchLanguage && onSwitchLanguage && (
        <button
          type="button"
          onClick={onSwitchLanguage}
          className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] hover:bg-[hsl(var(--border))]/40 text-[hsl(var(--foreground))] text-xs font-medium transition-colors"
        >
          <Languages size={14} strokeWidth={2} />
          {t("philology.switchLanguage")}
        </button>
      )}
    </div>
  );
}
